import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { RotateCcw, X } from 'lucide-react';

type TutorialVideoModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  youtubeId: string;
  title: string;
};

type YtPlayer = {
  destroy: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  playVideo: () => void;
};

type YtNamespace = {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string;
      width?: string | number;
      height?: string | number;
      playerVars?: Record<string, string | number>;
      events?: {
        onStateChange?: (event: { data: number }) => void;
      };
    }
  ) => YtPlayer;
  PlayerState: { ENDED: number };
};

declare global {
  interface Window {
    YT?: YtNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeApiPromise: Promise<YtNamespace> | null = null;

function loadYouTubeApi(): Promise<YtNamespace> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('YouTube API requires a browser'));
  }
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  });

  return youtubeApiPromise;
}

const TutorialVideoModal: React.FC<TutorialVideoModalProps> = ({
  open,
  onOpenChange,
  youtubeId,
  title,
}) => {
  const playerRef = useRef<YtPlayer | null>(null);
  const [hostEl, setHostEl] = useState<HTMLDivElement | null>(null);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open || !hostEl) {
      setEnded(false);
      return;
    }

    let cancelled = false;
    const mount = document.createElement('div');
    mount.style.width = '100%';
    mount.style.height = '100%';
    hostEl.appendChild(mount);

    loadYouTubeApi().then((YT) => {
      if (cancelled) return;

      playerRef.current = new YT.Player(mount, {
        videoId: youtubeId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          playsinline: 1,
          origin: window.location.origin,
        },
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.ENDED) {
              setEnded(true);
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch {
        // Player may already be torn down with the DOM node
      }
      playerRef.current = null;
      mount.remove();
      setEnded(false);
    };
  }, [open, youtubeId, hostEl]);

  const handleReplay = () => {
    const player = playerRef.current;
    if (!player) return;
    setEnded(false);
    player.seekTo(0, true);
    player.playVideo();
  };

  if (!open || typeof document === 'undefined') return null;

  // Portal to body so parent transforms (e.g. card hover:-translate-y) don't trap the overlay
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/70"
      onClick={() => onOpenChange(false)}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-[95vw] lg:max-w-6xl xl:max-w-7xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate pr-8">{title}</h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Cerrar tutorial"
            className="absolute top-2.5 right-2.5 z-20 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="relative w-full aspect-video bg-black max-h-[85vh]">
          <div ref={setHostEl} className="absolute inset-0 w-full h-full [&>iframe]:w-full [&>iframe]:h-full" />
          {ended && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-black px-4">
              <p className="text-white text-base sm:text-lg font-medium text-center">
                Tutorial finalizado
              </p>
              <button
                type="button"
                onClick={handleReplay}
                className="inline-flex items-center gap-2 rounded-lg bg-[#1CB3B3] hover:bg-[#189e9e] text-white px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1CB3B3]"
              >
                <RotateCcw className="w-4 h-4" />
                Ver de nuevo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TutorialVideoModal;
